import type { CSSProperties } from "react";
import { tapFeedback } from "../feedback";

export interface Tab<Id extends string> {
  id: Id;
  label: string;
  /** Shown over the label; the label alone is the button's accessible name. */
  emoji: string;
}

/**
 * The floating pill at the foot of the screen: one button per tab, emoji over a short label. A
 * lighter capsule sits behind the active tab and slides to the next one; it is a single element
 * moved by its index, so the slide is one CSS transform rather than a capsule per tab.
 */
export function TabBar<Id extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: readonly Tab<Id>[];
  active: Id;
  onChange: (id: Id) => void;
}) {
  const index = Math.max(0, tabs.findIndex((t) => t.id === active));
  const place = { "--tabs": tabs.length, "--at": index } as CSSProperties;

  return (
    <nav className="tabbar" aria-label="Main">
      <div className="tabbar__track" style={place}>
        <span className="tabbar__thumb" aria-hidden="true" />
        {tabs.map((t) => {
          const on = t.id === active;
          return (
            <button
              key={t.id}
              type="button"
              className="tabbar__tab"
              aria-current={on ? "page" : undefined}
              onClick={() => {
                // Every press does something: a new tab, or back to the top of this one.
                tapFeedback();
                onChange(t.id);
              }}
            >
              <span className="tabbar__emoji" aria-hidden="true">
                {t.emoji}
              </span>
              <span className="tabbar__label">{t.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
